import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Collapse, IconButton, Breadcrumbs, Stack,
  Button,
  tableCellClasses,
  styled
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ImageIcon from '@mui/icons-material/Image';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'shared/lib/hooks/hooks';
import { getMarathonDetails } from 'entities/admin/model/thunk/getMarathonDetails';
import styles from './viewMarathon.module.scss';
import dayjs from 'dayjs';
import axiosApi from 'shared/api/axiosApi';
import { checkCurrentLanguage } from 'shared/lib/checkCurrentLanguage';
import { serverURL } from "../../shared/lib/serverURL";
import { blockRegisterToDistance } from 'entities/admin/model/thunk/blockRegisterToDistance';
import PlayCircleFilledWhiteOutlinedIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import PauseCircleOutlineOutlinedIcon from '@mui/icons-material/PauseCircleOutlineOutlined';
import { participantListTableCell } from 'entities/admin/lib/MUIStyles/MUIStyles';
import { BagIcon, Calendar3Icon, CupIcon, LocationIcon, OrdinaryCalendarIcon, ProfileIcon } from 'assets/icons';
import { useTranslation } from 'react-i18next';
import {
  ADMIN_PANEL_PATH,
  VOUCHERS_ADMIN_PATH,
  VOUCHERS_PROMOCODES_PATH,
} from "pages/paths";
import { RootState } from 'shared/config/configureStore';

const ViewMarathon = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [marathon, setMarathon] = useState<any>(null);
  const [image, setImage] = useState<any>(null);

  const userRole = useAppSelector((state: RootState) => state.users.userRole);

  const fetchImage = async () => {
    try {
      const response = await axiosApi.get(`marathons/${id}`, {
        headers: {
          "Accept-Language": checkCurrentLanguage(),
        },
      });
      return response.data.logo;
    } catch (error) {
      console.error('Error fetching image:', error);
      return null;
    }
  }

  useEffect(() => {
    const loadImage = async () => {
      const image = await fetchImage();
      setImage(image);
    };
    loadImage();
  }, []);

  useEffect(() => {
    if (!id) return;
    dispatch(getMarathonDetails(+id)).then((res) => {
      if ('payload' in res && res.payload) {
        setMarathon(res.payload);
      }
    });
  }, [id, i18n.language]);

  const formatDate = (date: string) => dayjs(date).format('DD.MM.YYYY');


  const handleBlockRegister = async (distanceId?: number, isActive?: boolean) => {
    try {
      await dispatch(blockRegisterToDistance({
        distanceId: distanceId,
        isActive: !isActive,
      })).unwrap();

      if (id) {
        dispatch(getMarathonDetails(+id)).then((res) => {
          if ('payload' in res && res.payload) {
            setMarathon(res.payload);
          }
        });
      }
    } catch (error) {
      console.error('Ошибка при блокировке регистрации:', error);
    }
  };

  const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#ECF2F7",
      color: "#3E444A",
      padding: "8px",
      fontFamily: "Raleway",
      fontSize: "12px",
      lineHeight: "20px",
      border: "none",
      fontWeight: "700 !important",
      "@media(max-width: 1000px)": {
        // fontSize: '8px',
      },
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(() => ({
    "&:nth-of-type(odd)": {
      backgroundColor: "#F5FAFF",
    },
    "&:hover": { cursor: "pointer" },
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  return (
    <Box className={styles.container}>
      <Stack spacing={2} sx={{ mb: '20px' }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <span onClick={() => navigate(-1)} className={styles.breadcrumbLink}>{t("marathonsPage.marathons")}</span>
          <Typography className={styles.breadcrumbCurrent}>{marathon?.name}</Typography>
        </Breadcrumbs>
      </Stack>


      {marathon && (
        <>
          <Paper sx={{ borderRadius: '20px' }} className={styles.generalInfoSection}>
            <Box className={styles.textInfo}>
              <Typography variant="h5" sx={{ paddingBottom: '24px' }} fontWeight={600} gutterBottom>{marathon.name}</Typography>
              <Typography><OrdinaryCalendarIcon />{t("viewMarathon.date")}<span className={styles.textDesc}>{formatDate(marathon.date)}</span></Typography>
              <Typography><LocationIcon />{t("viewMarathon.place")}<span className={styles.textDesc}>{marathon.place}</span></Typography>
              <Typography><OrdinaryCalendarIcon />{t("viewMarathon.acceptingPeriod")}<span className={styles.textDesc}>{formatDate(marathon.startDateAcceptingApplications)} — {formatDate(marathon.endDateAcceptingApplications)}</span></Typography>
              <Typography><Calendar3Icon />{t("viewMarathon.printDate")}<span className={styles.textDesc}>{formatDate(marathon.participantNumbersPrintDate)}</span></Typography>
              <Typography><BagIcon />{t("viewMarathon.kit")}<span className={styles.textDesc}>{marathon.startKitMessage}</span></Typography>
              <Typography><ProfileIcon /> {t("viewMarathon.totalPlaces")}<span className={styles.textDesc}>{marathon.totalPlaces}</span></Typography>
              <Typography>
                <CupIcon />{t("viewMarathon.isActive")}{" "}
                <span className={styles.textDesc}>
                  <span className={`${styles.statusBtn} ${new Date() > new Date(marathon.date) ? styles.inactive : marathon.isActive ? styles.active : styles.inactive}`}>
                    {new Date() > new Date(marathon.date) ? t("marathonCard.status_completed") : marathon.isActive ? t("viewMarathon.active") : t("viewMarathon.suspended")}
                  </span>
                </span>
              </Typography>
            </Box>
            <Box className={styles.imageWrapper}>
              {image ? (
                <img src={serverURL + image} alt="Marathon" className={styles.marathonImage} />
              ) : (
                <Box className={styles.placeholderImage}><ImageIcon fontSize="large" /></Box>
              )}
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '32px' }}>
            {marathon.distances.map((distance: any) => (
              <Paper sx={{ borderRadius: '20px' }}>
                <Box className={styles.distanceSection} key={distance.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
                    <Typography sx={{ fontSize: '24px' }} className={styles.distanceTitle} fontWeight={600} mb={1}>{t("viewMarathon.distance")} {distance.name}</Typography>
                    {(userRole === 'Owner' || userRole === 'Admin') && (
                      <Button
                        variant="contained"
                        sx={{
                          borderRadius: '12px',
                          padding: '12px 18px',
                          minWidth: 'unset',
                          height: '40px',
                          display: 'flex',
                          backgroundColor: "#0090D1"
                        }}
                        onClick={() => handleBlockRegister(distance?.id, distance?.isActive)}>
                        {distance?.isActive ?
                          <>
                            <PauseCircleOutlineOutlinedIcon sx={{ marginRight: '8px' }} /><Typography className={styles.blockBtn}>{t("viewMarathon.stopRegistration")}</Typography>
                          </> : <><PlayCircleFilledWhiteOutlinedIcon sx={{ marginRight: '8px' }} />
                            <Typography className={styles.blockBtn}>{t("viewMarathon.resumeRegistration")}</Typography></>}
                      </Button>
                    )}
                  </Box>
                  <TableContainer
                    component={Paper}
                    sx={{
                      boxShadow: "none",
                      maxHeight: "80vh",
                      marginTop: "24px",
                      borderRadius: "12px 12px 0 0",
                    }}
                  >
                    <Table size="small" className={styles.table} aria-label="customized table" stickyHeader>
                      <TableHead>
                        <StyledTableRow>
                          <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.totalParticipants")}</Typography></StyledTableCell>
                          <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.registeredParticipantsWithoutInsurance")}</Typography></StyledTableCell>
                          <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.registeredParticipantsWithInsurance")}</Typography></StyledTableCell>
                          <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.remainingPlaces")}</Typography></StyledTableCell>
                          <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.isActive2")}</Typography></StyledTableCell>
                          <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.insurance")}</Typography></StyledTableCell>
                        </StyledTableRow>
                      </TableHead>
                      <TableBody>
                        <StyledTableRow>
                          <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableDesc}>{distance.amountOfParticipants}</Typography></StyledTableCell>
                          <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableDesc}>{distance.registeredParticipantsWithoutInsurance}</Typography></StyledTableCell>
                          <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableDesc}>{distance.registeredParticipantsWithInsurance}</Typography></StyledTableCell>
                          <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableDesc}>{distance.remainingPlaces}</Typography></StyledTableCell>
                          <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableDesc}>{distance.isActive ? t("viewMarathon.yes") : t("viewMarathon.no")}</Typography></StyledTableCell>
                          <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableDesc}>{distance.hasInsurance ? t("viewMarathon.yes") : t("viewMarathon.no")}</Typography></StyledTableCell>
                        </StyledTableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <CollapsibleSection title={t("viewMarathon.ageCategories")}>
                    <TableContainer
                      component={Paper}
                      sx={{
                        boxShadow: "none",
                        maxHeight: "80vh",
                        borderRadius: "12px 12px 0 0",
                      }}
                    >
                      <Table size="small" className={styles.table}>
                        <TableHead>
                          <StyledTableRow>
                            <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.gender")}</Typography></StyledTableCell>
                            <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.ageFrom")}</Typography></StyledTableCell>
                            <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.ageTo")}</Typography></StyledTableCell>
                          </StyledTableRow>
                        </TableHead>
                        <TableBody>
                          {distance?.distanceAges?.length ? (
                            distance?.distanceAges?.map((age: any) => (
                              <StyledTableRow key={age.id}>
                                <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableDesc}>{age.gender ? t("viewMarathon.male") : t("viewMarathon.female")}</Typography></StyledTableCell>
                                <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableDesc}>{age.ageFrom}</Typography></StyledTableCell>
                                <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableDesc}>{age.ageTo}</Typography></StyledTableCell>
                              </StyledTableRow>
                            ))
                          ) : (
                            <StyledTableRow>
                              <StyledTableCell colSpan={5} align="center">
                                <Typography className={styles.tableDesc}>{t("viewMarathon.noAgeCategories")}</Typography>
                              </StyledTableCell>
                            </StyledTableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CollapsibleSection>

                  <CollapsibleSection title={t("viewMarathon.vouchers")}>
                    <TableContainer
                      component={Paper}
                      sx={{
                        boxShadow: "none",
                        maxHeight: "80vh",
                        borderRadius: "12px 12px 0 0",
                      }}
                    >
                      <Table size="small" className={styles.table}>
                        <TableHead>
                          <StyledTableRow>
                            <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.name")}</Typography></StyledTableCell>
                            <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.total")}</Typography></StyledTableCell>
                            <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.activated")}</Typography></StyledTableCell>
                            <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.notActivated")}</Typography></StyledTableCell>
                            <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.type")}</Typography></StyledTableCell>
                            <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.activeUntil")}</Typography></StyledTableCell>
                            <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.status")}</Typography></StyledTableCell>
                            <StyledTableCell sx={participantListTableCell}><Typography className={styles.tableTitle}>{t("viewMarathon.discount")}</Typography></StyledTableCell>
                          </StyledTableRow>
                        </TableHead>
                        <TableBody>
                          {distance?.vouchers?.length ? (
                            distance.vouchers.map((v: any) => {
                              const link = v.canCreateSubVoucher
                                ? `/${ADMIN_PANEL_PATH}/${VOUCHERS_ADMIN_PATH}/${v.id}/${encodeURIComponent(marathon.name)}/${encodeURIComponent(v.name ?? "")}/${encodeURIComponent(v.corporateClientName ?? "")}`
                                : `/${ADMIN_PANEL_PATH}/${VOUCHERS_PROMOCODES_PATH}/${v.id}`;

                              return (
                                <StyledTableRow key={v.id}>
                                  <StyledTableCell sx={participantListTableCell}>
                                    <Link
                                      to={link}
                                      state={{ marathonId: marathon.id }}
                                      className={styles.breadcrumbLink}
                                      style={{
                                        fontSize: "16px",
                                        fontWeight: 500,
                                        padding: "8px 0 8px 12px",
                                      }}
                                    >
                                      {v.name}
                                    </Link>
                                  </StyledTableCell>
                                  <StyledTableCell sx={participantListTableCell}>
                                    <Typography className={styles.tableDesc}>{v.quantity}</Typography>
                                  </StyledTableCell>
                                  <StyledTableCell sx={participantListTableCell}>
                                    <Typography className={styles.tableDesc}>{v.activatedCount}</Typography>
                                  </StyledTableCell>
                                  <StyledTableCell sx={participantListTableCell}>
                                    <Typography className={styles.tableDesc}>{v.notActivatedCount}</Typography>
                                  </StyledTableCell>
                                  <StyledTableCell sx={participantListTableCell}>
                                    <Typography className={styles.tableDesc}>
                                      {v.startKitIssueType === null
                                        ? t("viewMarathon.subVoucher")
                                        : v.startKitIssueType === 0
                                          ? t("viewMarathon.mainIndividual")
                                          : t("viewMarathon.mainCorporate")}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell sx={participantListTableCell}>
                                    <Typography className={styles.tableDesc}>
                                      {dayjs(v.expirationDate).format("DD.MM.YYYY")}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell sx={participantListTableCell}>
                                    <Typography className={styles.tableDesc}>
                                      <span className={`${styles.statusBtn} ${v.isActive ? styles.active : styles.inactive}`}>
                                        {v.isActive ? t("viewMarathon.active") : t("viewMarathon.inactive")}
                                      </span>
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell sx={participantListTableCell}>
                                    <Typography className={styles.tableDesc}>{v.discount} %</Typography>
                                  </StyledTableCell>
                                </StyledTableRow>
                              );
                            })
                          ) : (
                            <StyledTableRow>
                              <StyledTableCell colSpan={8} align="center">
                                <Typography className={styles.tableDesc}>{t("viewMarathon.noVouchers")}</Typography>
                              </StyledTableCell>
                            </StyledTableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CollapsibleSection>
                </Box>
              </Paper>
            )
            )}
          </Box>
        </>
      )
      }
    </Box >
  );
};

const CollapsibleSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);

  return (
    <Paper elevation={0} className={styles.wrapper}>
      <Box className={styles.header} onClick={() => setOpen(!open)}>
        <Typography sx={{ fontSize: '20px', fontWeight: "600", paddingTop: '10px', paddingLeft: '0' }} className={styles.title}>{title}</Typography>
        <IconButton size="small">
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box className={styles.content}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ViewMarathon;
